import * as React from 'react';
import { formatDuration, parseDuration, WEEKDAY_KEYS, weeklyContractSeconds } from '@cfl/shared';

import {
  Alert, AlertDescription, Button, Combobox, Dialog, DialogContent, DialogFooter,
  DialogHeader, DialogTitle, Input, Label, Skeleton,
} from '@/components/cargofl-ui';
import { ApiError } from '@/lib/api';
import { useContract, useHolidayGroups, useSaveContract } from '@/lib/admin-api';

const DAY_LABEL: Record<string, string> = {
  mon: 'Mon', tue: 'Tue', wed: 'Wed', thu: 'Thu', fri: 'Fri', sat: 'Sat', sun: 'Sun',
};

/** What a new contract starts as: the 40-hour week 79 of 80 people are on. */
const DEFAULT_HOURS: Record<string, string> = {
  mon: '8:00', tue: '8:00', wed: '8:00', thu: '8:00', fri: '8:00', sat: '0:00', sun: '0:00',
};

/**
 * The working contract.
 *
 * This is the most consequential form in the app: these seven numbers are the
 * denominator of every overtime figure the person will ever see, and the mode
 * decides how an absence is counted. So the dialog states the weekly total as it is
 * typed, and spells out what each mode does rather than showing two opaque options.
 */
export function ContractDialog({
  employee,
  onClose,
}: {
  employee: { id: string; displayName: string } | null;
  onClose: () => void;
}) {
  const { data: contract, isPending } = useContract(employee?.id ?? null);
  const { data: groups } = useHolidayGroups();
  const save = useSaveContract();

  const [hours, setHours] = React.useState<Record<string, string>>(DEFAULT_HOURS);
  const [entitlement, setEntitlement] = React.useState('20');
  const [groupId, setGroupId] = React.useState<string | null>(null);
  const [mode, setMode] = React.useState<'COMPENSATE' | 'REDUCE'>('COMPENSATE');
  const [start, setStart] = React.useState('');
  const [end, setEnd] = React.useState('');
  const [error, setError] = React.useState<string | null>(null);

  // Loaded from the existing contract once it arrives; a person with none gets the
  // standard week as a starting point rather than an empty form.
  React.useEffect(() => {
    if (!employee || isPending) return;
    setError(null);
    if (contract) {
      setHours(
        Object.fromEntries(
          WEEKDAY_KEYS.map((k) => [k, formatDuration(contract.hoursPerDay[k] ?? 0)]),
        ),
      );
      setEntitlement(String(contract.vacationDaysPerYear));
      setGroupId(contract.holidayGroup?.id ?? null);
      setMode(contract.absenceCalculationMode);
      setStart(contract.workStartDate ?? '');
      setEnd(contract.workEndDate ?? '');
    } else {
      setHours(DEFAULT_HOURS);
      setEntitlement('20');
      setGroupId(groups?.[0]?.id ?? null);
      setMode('COMPENSATE');
      setStart('');
      setEnd('');
    }
  }, [employee, contract, isPending, groups]);

  // Parsed live so the weekly total is honest about what will be saved, and an
  // unparseable box shows as such before the request is made.
  const parsed = React.useMemo(() => {
    const out: Record<string, number | null> = {};
    for (const key of WEEKDAY_KEYS) {
      const text = (hours[key] ?? '').trim();
      out[key] = text === '' ? 0 : parseDuration(text);
    }
    return out;
  }, [hours]);

  const bad = WEEKDAY_KEYS.filter((k) => parsed[k] === null);
  const weekly = bad.length
    ? null
    : weeklyContractSeconds(
        Object.fromEntries(WEEKDAY_KEYS.map((k) => [k, parsed[k] ?? 0])) as never,
      );

  if (!employee) return null;

  const submit = async (event: React.FormEvent) => {
    event.preventDefault();
    setError(null);
    if (bad.length > 0) {
      setError(`Could not read ${bad.map((k) => DAY_LABEL[k]).join(', ')}. Use 8:00 or 7.5.`);
      return;
    }
    // Number('') is 0, so a cleared box used to save 0 leave days without a word.
    if (String(entitlement).trim() === '') {
      setError('Enter the leave entitlement in days (0 if none).');
      return;
    }
    const days = Number(entitlement);
    if (!Number.isInteger(days) || days < 0) {
      setError('Leave entitlement must be a whole number of days.');
      return;
    }
    try {
      await save.mutateAsync({
        id: employee.id,
        hoursPerDay: Object.fromEntries(WEEKDAY_KEYS.map((k) => [k, parsed[k] ?? 0])),
        vacationDaysPerYear: days,
        holidayGroupId: groupId,
        workStartDate: start || null,
        workEndDate: end || null,
        absenceCalculationMode: mode,
      });
      onClose();
    } catch (err) {
      // The API's message names the offending day and rule, so it is shown as-is.
      setError(err instanceof ApiError ? err.message : 'Could not save that contract.');
    }
  };

  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>
            {contract ? 'Working contract' : 'Set up a working contract'} &mdash;{' '}
            {employee.displayName}
          </DialogTitle>
        </DialogHeader>

        {isPending ? (
          <Skeleton className="h-64 w-full" />
        ) : (
          <form onSubmit={submit} className="space-y-5">
            <div className="space-y-2">
              <div className="flex items-baseline justify-between">
                <Label>Hours per day</Label>
                <span className="ts-num text-sm text-muted-foreground">
                  {weekly === null ? '—' : `${formatDuration(weekly)} per week`}
                </span>
              </div>
              <div className="grid grid-cols-7 gap-1.5">
                {WEEKDAY_KEYS.map((key) => (
                  <div key={key} className="space-y-1">
                    <div className="text-center text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
                      {DAY_LABEL[key]}
                    </div>
                    <Input
                      aria-label={DAY_LABEL[key]}
                      value={hours[key] ?? ''}
                      onChange={(e) => setHours((h) => ({ ...h, [key]: e.target.value }))}
                      className={`ts-num px-1 text-center ${parsed[key] === null ? 'border-destructive' : ''}`}
                    />
                  </div>
                ))}
              </div>
              <p className="text-xs text-muted-foreground">
                Recorded in 15-minute steps. A zero day is a non-working day.
              </p>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label htmlFor="entitlement">Annual leave (days)</Label>
                <Input
                  id="entitlement"
                  className="ts-num"
                  value={entitlement}
                  onChange={(e) => setEntitlement(e.target.value)}
                />
              </div>
              <div className="space-y-1.5">
                <Label>Public holidays</Label>
                <Combobox
                  value={groupId}
                  onChange={setGroupId}
                  options={(groups ?? []).map((g) => ({
                    value: g.id,
                    label: g.name,
                    hint: `${g._count.holidays} dates`,
                  }))}
                  placeholder="No holiday calendar"
                  searchPlaceholder="Search calendars"
                />
              </div>
            </div>

            <div className="space-y-1.5">
              <Label>How absence is counted</Label>
              <Combobox
                value={mode}
                onChange={(v) => setMode(v as 'COMPENSATE' | 'REDUCE')}
                options={[
                  {
                    value: 'COMPENSATE',
                    label: 'As time worked',
                    description: 'Leave fills the day, so the target is still met',
                  },
                  {
                    value: 'REDUCE',
                    label: 'As a reduced target',
                    description: 'The day expects less, so an empty day is not a shortfall',
                  },
                ]}
                placeholder="Choose"
                searchPlaceholder="Search"
              />
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label htmlFor="joined">Joined (optional)</Label>
                <Input id="joined" type="date" value={start} onChange={(e) => setStart(e.target.value)} />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="left">Left (optional)</Label>
                <Input id="left" type="date" value={end} onChange={(e) => setEnd(e.target.value)} />
              </div>
            </div>
            <p className="text-xs leading-relaxed text-muted-foreground">
              Outside these dates the contract does not apply, so somebody who joined
              mid-month is not short the hours before they started.
            </p>

            {error && (
              <Alert className="border-destructive/30">
                <AlertDescription className="text-sm leading-relaxed">{error}</AlertDescription>
              </Alert>
            )}

            <DialogFooter>
              <Button type="button" variant="ghost" onClick={onClose}>Cancel</Button>
              <Button type="submit" disabled={save.isPending}>
                {save.isPending ? 'Saving…' : 'Save contract'}
              </Button>
            </DialogFooter>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
}
